"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Auth = void 0;
const web3_js_1 = require("@solana/web3.js");
const react_1 = require("react");
const common_1 = require("../common");
const accountParser_1 = require("../common/accountParser");
const hooks_1 = require("../common/hooks");
/** Class for interacting with the Jet Auth program */
class Auth {
    /**
     * Creates an instance of Auth.
     * @param {UserAuthentication} userAuthentication
     * @param {PublicKey} address
     * @memberof Auth
     */
    constructor(userAuthentication, address) {
        this.userAuthentication = userAuthentication;
        this.address = address;
    }
    /**
     * Dervices the user authentication account from the argued public key.
     * @static
     * @param {PublicKey} user
     * @returns {Promise<PublicKey>}
     * @memberof Auth
     */
    static deriveUserAuthentication(user) {
        return (0, common_1.findDerivedAccount)(Auth.PROGRAM_ID, user);
    }
    /**
     * Creates a connection to the authentication program.
     * @static
     * @param {Provider} provider
     * @returns {Promise<Program<AuthIdl>>}
     * @memberof Auth
     */
    static connect(provider) {
        return (0, common_1.connect)(Auth.PROGRAM_ID, provider);
    }
    /**
     * Load and return the user authentication account from the argued public key.
     * @static
     * @param {Program<AuthIdl>} authProgram
     * @param {PublicKey} user
     * @returns {Promise<Auth>}
     * @memberof Auth
     */
    static async loadUserAuth(authProgram, user) {
        const address = this.deriveUserAuthentication(user);
        const userAuthentication = (await authProgram.account.userAuthentication.fetch(address));
        return new Auth(userAuthentication, address);
    }
    /**
     * Custom React hook to use the authentication program as state.
     * @static
     * @param {Provider} provider
     * @returns {(Program<AuthIdl> | undefined)}
     * @memberof Auth
     */
    static useAuthProgram(provider) {
        return hooks_1.Hooks.usePromise(async () => Auth.connect(provider), [provider]);
    }
    /**
     * Load the user authentication account. The account will be fetched every 2 seconds until it has been authenticated.
     * @static
     * @param {Program<AuthIdl>} [authProgram]
     * @param {(PublicKey | null)} [wallet]
     * @returns {{ authAccount?: UserAuthentication; loading: boolean }}
     * @memberof Auth
     */
    static useAuthAccount(authProgram, wallet) {
        const [{ authAccount, loading }, setAuthAccount] = (0, react_1.useState)({
            authAccount: undefined,
            loading: true
        });
        (0, react_1.useEffect)(() => {
            let abort = false;
            const interval = setInterval(() => {
                if (!wallet || !authProgram) {
                    setAuthAccount({ authAccount: undefined, loading: true });
                    return;
                }
                if (authAccount && authAccount.userAuthentication.complete) {
                    clearInterval(interval);
                    return;
                }
                Auth.loadUserAuth(authProgram, wallet)
                    .then(newAccount => {
                    if (!abort) {
                        setAuthAccount({ authAccount: newAccount, loading: false });
                    }
                })
                    .catch(() => {
                    setAuthAccount({ authAccount: undefined, loading: false });
                });
            }, 1000);
            return () => {
                abort = true;
                clearInterval(interval);
            };
        }, [wallet === null || wallet === void 0 ? void 0 : wallet.toBase58(), authProgram, authAccount]);
        return { authAccount, loading };
    }
    /**
     * Create a new account that can be used to identify that a
     * wallet/address is properly authenticated to perform protected actions.
     * @static
     * @param {Program<AuthIdl>} authProgram
     * @param {PublicKey} user
     * @param {PublicKey} payer
     * @returns {Promise<Transaction>}
     * @memberof Auth
     */
    static async createUserAuth(authProgram, user, payer) {
        const auth = this.deriveUserAuthentication(user);
        return authProgram.methods.createUserAuth().accounts({ user, payer, auth }).transaction();
    }
    /**
     * Authenticate a user address. Auth.AUTHORITY must sign.
     * @static
     * @param {Program<AuthIdl>} authProgram
     * @param {PublicKey} user
     * @returns {Promise<Transaction>}
     * @memberof Auth
     */
    static approveAuthentication(authProgram, user) {
        const authority = authProgram.provider.wallet.payer;
        return authProgram.methods
            .authenticate()
            .accounts({
            auth: user,
            authority: authority.publicKey
        })
            .signers([authority])
            .transaction();
    }
}
exports.Auth = Auth;
/** Auth Program ID */
Auth.PROGRAM_ID = new web3_js_1.PublicKey("JPALXR88jy2fG3miuu4n3o8Jef4K2Cgc3Uypr3Y8RNX");
/** Hardcoded address of the authority that can authenticate users */
Auth.AUTHORITY = new accountParser_1.PubkeyField("JPALXR88jy2fG3miuu4n3o8Jef4K2Cgc3Uypr3Y8RNX");
//# sourceMappingURL=auth.js.map